'use client';

import { t } from 'mobx-state-tree';

import type { HideLoadingOverlayOptions, ShowLoadingOverlayOptions } from '@/app/global/loadingOverlayStoreModel';
import type { UpdaterState } from '@/app/sdk';
import type { UserInfo } from '@/context/AuthContext';
import type { Instance } from 'mobx-state-tree';

import { CurrentUserStoreModel } from '@/app/global/currentUserStoreModel';
import { EditorErrorsStoreModel } from '@/app/global/editorErrorsStoreModel';
import { LoadingOverlayStoreModel } from '@/app/global/loadingOverlayStoreModel';
import { UpdaterStoreModel } from '@/editor/updater/updaterStoreModel';


export { MIN_LOADING_OVERLAY_DISPLAY_MS } from '@/app/global/loadingOverlayStoreModel';
export type { HideLoadingOverlayOptions, ShowLoadingOverlayOptions } from '@/app/global/loadingOverlayStoreModel';

/** Global store model with loading overlay state and current user */
const GlobalStore = t
  .model('GlobalStore', {
    loadingOverlayState: t.optional(LoadingOverlayStoreModel, {}),
    currentUser: t.optional(CurrentUserStoreModel, {}),
    errors: t.optional(EditorErrorsStoreModel, {}),
    updater: t.optional(UpdaterStoreModel, {}),
  })
  .views((self) => ({
    get loadingOverlay() {
      return self.loadingOverlayState.loadingOverlay;
    },
    get loadingOverlayMessage() {
      return self.loadingOverlayState.loadingOverlayMessage;
    },
    get pinnedLoadingOverlayMessage() {
      return self.loadingOverlayState.pinnedLoadingOverlayMessage;
    },
    get loadingOverlayDisplayMessages() {
      return self.loadingOverlayState.loadingOverlayDisplayMessages;
    },
    get loadingOverlayUseDimmedBackdrop() {
      return self.loadingOverlayState.loadingOverlayUseDimmedBackdrop;
    },
    get loadingOverlayPinnedMessages() {
      return self.loadingOverlayState.loadingOverlayPinnedMessages;
    },
    get loadingOverlayUnpinnedMessages() {
      return self.loadingOverlayState.loadingOverlayUnpinnedMessages;
    },
    get pinnedOverlayIds() {
      return self.loadingOverlayState.pinnedOverlayIds;
    },
    get unpinnedOverlayIds() {
      return self.loadingOverlayState.unpinnedOverlayIds;
    },
    get loadingOverlayUseDimmedBackdropById() {
      return self.loadingOverlayState.loadingOverlayUseDimmedBackdropById;
    },
    get loadingOverlayUnpinnedMessageQueues() {
      return self.loadingOverlayState.loadingOverlayUnpinnedMessageQueues;
    },
    get loadingOverlayUnpinnedCloseAfterCurrent() {
      return self.loadingOverlayState.loadingOverlayUnpinnedCloseAfterCurrent;
    },
    get buildErrors() { return self.errors.buildErrors; },
    get showBuildErrorOverlay() { return self.errors.showBuildErrorOverlay; },
    get playModeErrors() { return self.errors.playModeErrors; },
    get playModeFatalErrors() { return self.errors.playModeFatalErrors; },
    get playModeErrorOrder() { return self.errors.playModeErrorOrder; },
    get showPlayModeErrorOverlay() { return self.errors.showPlayModeErrorOverlay; },
    get suppressNextPlayModeOverlay() { return self.errors.suppressNextPlayModeOverlay; },
    get renderErrorName() { return self.errors.renderErrorName; },
    get renderError() { return self.errors.renderError; },
    get renderErrorStack() { return self.errors.renderErrorStack; },
    get buildErrorCount() { return self.errors.buildErrorCount; },
    get hasBuildErrors() { return self.errors.hasBuildErrors; },
    get hasPlayModeErrors() { return self.errors.hasPlayModeErrors; },
    get hasPlayModeFatalErrors() { return self.errors.hasPlayModeFatalErrors; },
    get hasAnyPlayModeErrors() { return self.errors.hasAnyPlayModeErrors; },
    get playModeErrorCount() { return self.errors.playModeErrorCount; },
    get allPlayModeErrors() {
      return self.errors.allPlayModeErrors;
    },
    get hasRenderError() { return self.errors.hasRenderError; },
    get showUpdaterPill() {
      return self.updater.showUpdaterPill;
    },
    get updaterPhase() {
      return self.updater.phase;
    },
    get updaterTrigger() {
      return self.updater.trigger;
    },
    get updaterCurrentVersion() {
      return self.updater.currentVersion;
    },
    get updaterTargetVersion() {
      return self.updater.targetVersion;
    },
    get updaterReleaseDate() {
      return self.updater.releaseDate;
    },
    get updaterReleaseName() {
      return self.updater.releaseName;
    },
    get updaterReleaseNotes() {
      return self.updater.releaseNotes;
    },
    get updaterReleaseUrl() {
      return self.updater.releaseUrl;
    },
    get updaterProgressPercent() {
      return self.updater.progressPercent;
    },
    get updaterTransferredBytes() {
      return self.updater.transferredBytes;
    },
    get updaterTotalBytes() {
      return self.updater.totalBytes;
    },
    get updaterErrorMessage() {
      return self.updater.errorMessage;
    },
    get updaterCheckedAt() {
      return self.updater.checkedAt;
    },
    get updaterUpdatedAt() {
      return self.updater.updatedAt;
    },
    get updaterPhaseStartedAt() {
      return self.updater.phaseStartedAt;
    },
    get updaterElapsedSeconds() {
      return self.updater.elapsedSeconds;
    },
    get updaterDownloadRequestedAt() {
      return self.updater.downloadRequestedAt;
    },
    get updaterRetryAttempt() {
      return self.updater.retryAttempt;
    },
    get isUpdaterMockEnabled() {
      return self.updater.isMockEnabled;
    },
    get activeMockScenarioId() {
      return self.updater.activeMockScenarioId;
    },
    get updaterState(): UpdaterState {
      return self.updater.updaterState;
    },
  }))
  .actions((self) => ({
    showLoadingOverlay: (id: string, options?: ShowLoadingOverlayOptions) => {
      self.loadingOverlayState.showLoadingOverlay(id, options);
    },
    hideLoadingOverlay: (id: string, pinned?: boolean, options?: HideLoadingOverlayOptions) => {
      self.loadingOverlayState.hideLoadingOverlay(id, pinned, options);
    },
    completeUnpinnedOverlayHead(id: string) {
      self.loadingOverlayState.completeUnpinnedOverlayHead(id);
    },
    setCurrentUser(uid: string | null, userInfo: UserInfo | null) {
      self.currentUser.setCurrentUser(uid, userInfo);
    },
    setBuildErrors(errors: string[]) {
      self.errors.setBuildErrors(errors);
    },
    addBuildError(message: string) {
      self.errors.addBuildError(message);
    },
    clearBuildErrors() { self.errors.clearBuildErrors(); },
    setShowBuildErrorOverlay(show: boolean) { self.errors.setShowBuildErrorOverlay(show); },
    addPlayModeError(message: string) {
      self.errors.addPlayModeError(message);
    },
    addPlayModeFatalError(message: string) {
      self.errors.addPlayModeFatalError(message);
    },
    markPlayModeFatal() {
      self.errors.markPlayModeFatal();
    },
    clearPlayModeErrors() {
      self.errors.clearPlayModeErrors();
    },
    setShowPlayModeErrorOverlay(show: boolean) {
      self.errors.setShowPlayModeErrorOverlay(show);
    },
    setSuppressNextPlayModeOverlay(suppress: boolean) {
      self.errors.setSuppressNextPlayModeOverlay(suppress);
    },
    consumeSuppressNextPlayModeOverlay() {
      return self.errors.consumeSuppressNextPlayModeOverlay();
    },
    setRenderError(name: string | null, message: string | null, stack: string | null) {
      self.errors.setRenderError(name, message, stack);
    },
    clearRenderError() {
      self.errors.clearRenderError();
    },
    setUpdaterState(state: UpdaterState) {
      self.updater.setUpdaterState(state);
    },
    setUpdaterMockEnabled(enabled: boolean) {
      self.updater.setUpdaterMockEnabled(enabled);
    },
    setActiveMockScenarioId(id: string | null) {
      self.updater.setActiveMockScenarioId(id);
    },
  }));

export type IGlobalStore = Instance<typeof GlobalStore>

/** Create the global store instance */
export const globalStore = GlobalStore.create();
